import { Box, Card, CardContent, Grid, Typography } from "@mui/material";
import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import SettingsReport from "./SettingsReport";
import {
  PerformanceReportTypeEnum,
  BucketPairReportStatus,
  BucketReportStatusEnum,
  BucketReportRequest,
  ExcelDownloadVariant,
  ReportThemedCardConfig,
} from "helpers/types";
import ChartsReport from "./Charts/ChartsReport";
import { ChartSpec } from "./Charts/chartSeriesSelection";
import { ReportJobStatus } from "constants/index";
import { StrategyReportThemedCardBlurbs } from "helpers/constants";
import MultiValuesCard from "./MultiValuesCard";
import CurrentAllocationChartReport from "./Charts/CurrentAllocationChartReport";
import { DrawdownComparisonChart } from "./Charts/DrawdownComparisonChart";
import { useSettingsReportValue } from "hooks/useReportStorage";
import {
  SETTINGS_ACCOUNT_IDS_KEY,
  SETTINGS_CLIENT_ID_KEY,
} from "./ClientPicker";
import { useFederalTaxRate } from "hooks/useFederalTaxRate";
import { useStateTaxRate } from "hooks/useStateTaxRate";
import {
  useStartBucketReportJobMutation,
  useLazyGetBucketReportJobStatusQuery,
  useGetReportPdfMutation,
} from "features/trading/accounts";
import { BucketNumberEnum } from "features/trading/buckets";
import BucketSummarySelect from "components/buckets/BucketSummarySelect";
import { pollReportJob, reportJobErrorMessage } from "./reportJobPolling";
import {
  ReportSettingsProvider,
  useReportSettings,
} from "./ReportSettingsContext";
import TransactionModal from "./TransactionModal";
import RequestNotification from "components/ui/RequestNotification";
import { extractReportErrorMessage } from "./extractReportErrorMessage";
import { useInFlightCalculations } from "./useInFlightCalculations";
import { useSummaryCards } from "./strategy/useSummaryCards";
import { useKeepCard } from "./strategy/useKeepCard";
import { useProtectCard } from "./strategy/useProtectCard";
import { useDiversifyCard } from "./strategy/useDiversifyCard";
import { useHarvestCard } from "./strategy/useHarvestCard";
import {
  PDF_DOWNLOAD_FAILED_MESSAGE,
  buildStrategyPdfRequest,
  downloadPdfAvailability,
} from "./strategy/pdfExport";
import { openDownloadUrl } from "./strategy/openDownloadUrl";
import { pairFailuresMessage } from "./strategy/pairFailuresMessage";
import ReportActions from "./strategy/ReportActions";
import StrategyReportSettings from "./strategy/StrategyReportSettings";
import CalculatePrompt from "./strategy/CalculatePrompt";
import SummaryCardsGrid from "./strategy/SummaryCardsGrid";

// The report's figure rules live beside the hooks that hold its state, under
// ./strategy, one module per card. They are re-exported here because this is
// where callers and tests have always imported them from.
export type { KeepFiguresState, KeepRowKey } from "./strategy/keepFigures";
export {
  KEEP_NO_BUCKET_1,
  keepParticipationFigure,
  keepRowFigure,
} from "./strategy/keepFigures";
export type { DiversifyStockConcentrations } from "./strategy/diversifyFigures";
export {
  DIVERSIFY_NO_BUCKET_1,
  beginningConcentrationFigure,
  concentrationRowLabel,
  currentConcentrationFigure,
  diversifyStockFigure,
} from "./strategy/diversifyFigures";
export type { LongValueSummaryCards } from "./strategy/summaryFigures";
export {
  LONG_VALUE_VALUES_UNAVAILABLE,
  longValueCards,
  summaryValueDateNotes,
  timeWeightedReturnFigure,
  twrFormulaLabel,
} from "./strategy/summaryFigures";
export {
  PDF_DOWNLOAD_FAILED_MESSAGE,
  downloadPdfAvailability,
  pdfFigureValue,
} from "./strategy/pdfExport";
export { sellableAsOfCaption } from "./strategy/harvestFigures";

// A themed card as the screen renders it: the config the PDF also prints,
// plus the blurb and the chart that only the screen shows.
interface ThemedCardView {
  config: ReportThemedCardConfig;
  blurb: string;
  chart: ReactNode;
}

function StrategyReportContent() {
  // Which client's report this is, read from the SETTINGS STORE the picker
  // writes rather than from `settingsInfo` -- the settings context lives
  // inside the same unmounting tab panel and starts empty on every remount,
  // so keying on it would read as an account change on every tab switch. The
  // useBeginningConcentration docstring carries the rest of that reasoning.
  const [storedClientId] = useSettingsReportValue(
    `${SETTINGS_CLIENT_ID_KEY}_strategy`,
    "",
  );
  const [storedAccountIds] = useSettingsReportValue(
    `${SETTINGS_ACCOUNT_IDS_KEY}_strategy`,
    "[]",
  );
  // WHICH REPORT the uncalculated rows' typed figures belong to, stated once
  // because two cards key on it: Beginning Concentration's own account
  // scoping (task 3141) and the Harvest rows the report-scoped holder keeps
  // (useUncalculatedReportValue). A figure typed against one client is not a claim
  // about the next, and this is what drops it when the client changes.
  const reportScopeKey = `${storedClientId}:${storedAccountIds}`;

  const summary = useSummaryCards();
  const keep = useKeepCard();
  const protect = useProtectCard();
  const diversify = useDiversifyCard({
    reportScopeKey,
    keepShares: keep.keepShares,
    keepFiguresState: keep.keepFiguresState,
    // "A completed run has stored figures", read from the same persisted
    // inputs key the PDF button reads -- see beginningConcentrationFigure.
    hasStoredReport: summary.pdfReportInputsKey !== "",
  });
  const harvest = useHarvestCard(reportScopeKey);

  const [isTransactionModalOpen, setIsTransactionModalOpen] = useState(false);
  const [getReportPdf, { isLoading: isPdfLoading }] = useGetReportPdfMutation();
  // The series the performance chart is drawing, reported up by ChartsReport
  // so the PDF prints the chart the reader is looking at.
  const [chartSpecs, setChartSpecs] = useState<ChartSpec[]>([]);
  const { settingsInfo } = useReportSettings();
  // Both rates are edited in the Keep card's tax settings and sent with
  // Calculate, where they drive that card's tax savings.
  const [federalTaxRate] = useFederalTaxRate();
  const { stateTaxRate } = useStateTaxRate(settingsInfo.client_id ?? null);
  // Strategy reports run over the account's persisted Bucket 1/2 — no
  // Strategy selection involved. A run produces the combined excel (one
  // sheet per pair), per-pair statuses, the bucket totals and the themed
  // cards.
  const [startBucketReportJob] = useStartBucketReportJobMutation();
  const [fetchBucketJobStatus] = useLazyGetBucketReportJobStatusQuery();
  const {
    start: startCalculation,
    isCalculating,
    isCalculatingInputs,
  } = useInFlightCalculations();
  const [calculationError, setCalculationError] = useState("");
  const [pairFailures, setPairFailures] = useState<BucketPairReportStatus[]>(
    [],
  );
  const [excelUrl, setExcelUrl] = useState<string | null>(null);
  // The request the run on screen was calculated from, so Download Excel's
  // "Excel Live Formulas" re-runs exactly that report (task 5281) -- not the
  // inputs as they stand when it is picked. Cleared with excelUrl.
  const [excelRequest, setExcelRequest] =
    useState<BucketReportRequest | null>(null);
  // The same request, read when a live-formula job lands: one that lands
  // after the run on screen was replaced belongs to the old run, and is not
  // downloaded over the new one.
  const excelRequestRef = useRef<BucketReportRequest | null>(null);
  excelRequestRef.current = excelRequest;
  const [isPreparingLiveExcel, setIsPreparingLiveExcel] = useState(false);
  const [excelError, setExcelError] = useState("");
  // The option-pairing fix (task 3869), ON by default. Unlike the summary's
  // formula it changes what the backend CALCULATES, so it travels on the next
  // Calculate and retires the run on screen (see the reset effect below).
  const [optionPairingV2, setOptionPairingV2] = useState(true);

  // The four themed cards, in the order they render and print.
  const themedCards = useMemo(
    (): ThemedCardView[] => [
      {
        config: keep.card,
        blurb: StrategyReportThemedCardBlurbs.KEEP,
        chart: null,
      },
      {
        config: protect.card,
        blurb: StrategyReportThemedCardBlurbs.PROTECT,
        chart: (
          <DrawdownComparisonChart
            values={protect.card.values}
            limits={protect.maxDropLimits}
            onLimitsChange={protect.setMaxDropLimits}
          />
        ),
      },
      {
        config: diversify.card,
        blurb: StrategyReportThemedCardBlurbs.DIVERSIFY,
        chart: (
          <CurrentAllocationChartReport
            value={diversify.currentConcentrationPct}
            unavailableReason={diversify.currentConcentrationUnavailable}
          />
        ),
      },
      {
        config: harvest.card,
        blurb: StrategyReportThemedCardBlurbs.HARVEST,
        chart: null,
      },
    ],
    [
      keep.card,
      protect.card,
      protect.maxDropLimits,
      protect.setMaxDropLimits,
      diversify.card,
      diversify.currentConcentrationPct,
      diversify.currentConcentrationUnavailable,
      harvest.card,
    ],
  );

  const themedCardsConfig = useMemo(
    () => [keep.card, protect.card, diversify.card, harvest.card],
    [keep.card, protect.card, diversify.card, harvest.card],
  );

  // A failure is shown, never thrown: an error thrown out of a click handler
  // reaches no one, which is how a failed download used to look like a
  // button that did nothing. The text is generic on purpose (operator, task
  // 4562) -- the endpoint answers a bad request with field errors, not a
  // sentence a reader can act on.
  const [pdfError, setPdfError] = useState("");
  const handleDownloadPdf = useCallback(async () => {
    try {
      const response = await getReportPdf(
        buildStrategyPdfRequest(
          chartSpecs,
          settingsInfo,
          summary.pdfClientInfo,
          themedCardsConfig,
        ),
      ).unwrap();
      if (response.download_url) {
        openDownloadUrl(response.download_url);
        return;
      }
      setPdfError(PDF_DOWNLOAD_FAILED_MESSAGE);
    } catch {
      setPdfError(PDF_DOWNLOAD_FAILED_MESSAGE);
    }
  }, [
    getReportPdf,
    chartSpecs,
    settingsInfo,
    summary.pdfClientInfo,
    themedCardsConfig,
  ]);

  const openTransactionModal = useCallback(() => {
    setIsTransactionModalOpen(true);
  }, []);

  const closeTransactionModal = useCallback(() => {
    setIsTransactionModalOpen(false);
  }, []);

  // The inputs a run is calculated against, as one key -- the same ones whose
  // change retires the run on screen in the reset effect below, so "a filter
  // changed" means one thing on this page. Calculate is inactive only while
  // a run against THESE inputs is in flight (useInFlightCalculations, task
  // 4553): change one mid-run and it can be pressed again for the new ones.
  // The formula is not among them: the gear is disabled for the whole run,
  // so it cannot change under one.
  const calculationInputsKey = JSON.stringify([
    settingsInfo.orion_account_id ?? null,
    settingsInfo.period?.[0] ?? null,
    settingsInfo.period?.[1] ?? null,
    settingsInfo.bucket_3_id ?? null,
    stateTaxRate ?? null,
    federalTaxRate ?? null,
    optionPairingV2,
  ]);
  const isCalculatingTheseInputs = isCalculatingInputs(calculationInputsKey);
  const pdfAvailability = downloadPdfAvailability(
    summary.pdfReportInputsKey,
    calculationInputsKey,
    isCalculatingTheseInputs,
  );

  const { twrFormula, applyRun: applySummaryRun } = summary;
  // Whether a run for the inputs on screen has landed. Nothing below the
  // action row is shown until one has (task 5001): before it every figure is
  // a placeholder a reader could take for the client's numbers. The run's
  // bucket totals are the marker -- held in memory only, set by a completed
  // run and cleared by any change of inputs (the reset effect below), so a
  // changed filter or a remount of the tab brings the prompt back. The run's
  // own inputs key must match too: a run that lands after a filter changed
  // carries the old inputs' figures, and an empty run stores no key at all.
  const isReportCalculated =
    summary.bucketTotals !== null &&
    summary.pdfReportInputsKey === calculationInputsKey;
  const { applyRun: applyKeepRun } = keep;
  const { applyRun: applyProtectRun } = protect;
  const { applyRun: applyDiversifyRun } = diversify;
  const { applyRun: applyHarvestRun } = harvest;

  // The request a run against the inputs on screen is calculated from, or
  // null while there is no account or period to run one for. Calculate sends
  // it, and so does Excel Live Formulas when no report is on screen (task
  // 5981): that workbook comes from a run of its own anyway, so it does not
  // wait for Calculate.
  const requestForInputs = useMemo((): BucketReportRequest | null => {
    const [start_date, end_date] = settingsInfo.period;
    if (!settingsInfo.orion_account_id || !start_date || !end_date) {
      return null;
    }
    // Buckets are CP-only: the backend runs one combined call+put report per
    // pair, so no option_type is sent. Bucket 1/2 belong to the page's
    // account; the selected Bucket 3 is passed by ID (it may belong to a
    // different account of the client) and adds one account-level sheet with
    // dynamically calculated values.
    return {
      orion_account_id: settingsInfo.orion_account_id,
      start_date,
      end_date,
      buckets: [BucketNumberEnum.BUCKET_1, BucketNumberEnum.BUCKET_2],
      ...(settingsInfo.bucket_3_id
        ? { bucket_3_id: settingsInfo.bucket_3_id }
        : {}),
      federal_tax_rate: federalTaxRate,
      ...(stateTaxRate != null ? { state_tax_rate: stateTaxRate } : {}),
      twr_formula: twrFormula,
      option_pairing_v2: optionPairingV2,
    };
  }, [
    settingsInfo.period,
    settingsInfo.orion_account_id,
    settingsInfo.bucket_3_id,
    federalTaxRate,
    stateTaxRate,
    twrFormula,
    optionPairingV2,
  ]);
  // The inputs' request, read when a live-formula job over it lands: one that
  // lands after any input changed -- the gear's formula included -- is not
  // downloaded over the new ones.
  const requestForInputsRef = useRef(requestForInputs);
  requestForInputsRef.current = requestForInputs;
  // The run's workbook and request count only while that run is the report on
  // screen: a run that lands after the inputs changed belongs to the old ones.
  const runExcelUrl = isReportCalculated ? excelUrl : null;
  const runExcelRequest = isReportCalculated ? excelRequest : null;
  // What Excel Live Formulas runs: the run on screen's own request when there
  // is one (task 5281), the inputs on screen otherwise.
  const liveExcelRequest = runExcelRequest ?? requestForInputs;

  // Backend Figures is the workbook Calculate already wrote. Excel Live
  // Formulas is not written by Calculate: the report is never persisted, so
  // its workbook comes from a job of its own over liveExcelRequest, which
  // calculates the report and is cached like any other run.
  const handleDownloadExcel = useCallback(
    async (variant: ExcelDownloadVariant) => {
      if (variant === "backend_figures") {
        if (runExcelUrl) openDownloadUrl(runExcelUrl);
        return;
      }
      const request = liveExcelRequest;
      if (!request) return;
      // A run's request is replaced by the next run; the inputs' request by
      // a change of inputs.
      const isStillOnScreen = runExcelRequest
        ? () => excelRequestRef.current === request
        : () => requestForInputsRef.current === request;
      const failure =
        "Failed to generate the live-formula Excel. Please try again.";
      setIsPreparingLiveExcel(true);
      try {
        const started = await startBucketReportJob({
          ...request,
          live_formulas: true,
        }).unwrap();
        const job = await pollReportJob(started, (key) =>
          fetchBucketJobStatus({ key }).unwrap(),
        );
        if (!isStillOnScreen()) return;
        if (job.status !== ReportJobStatus.COMPLETED || !job.result) {
          setExcelError(reportJobErrorMessage(job, failure));
          return;
        }
        openDownloadUrl(job.result.excel_url);
      } catch (e) {
        if (isStillOnScreen()) {
          setExcelError(extractReportErrorMessage(e, failure));
        }
      } finally {
        setIsPreparingLiveExcel(false);
      }
    },
    [
      runExcelUrl,
      runExcelRequest,
      liveExcelRequest,
      startBucketReportJob,
      fetchBucketJobStatus,
    ],
  );

  // The gear no longer re-runs the report (operator, task 3764): picking a
  // formula re-reads the run that is already on screen. What the gear's pick
  // still does is travel on the NEXT Calculate -- the workbook the Excel
  // button downloads is written under one formula, and `twrFormula` is what
  // says which.
  const handleCalculate = useCallback(async () => {
    // A copy: the run's request is what identifies the run on screen
    // (excelRequestRef), so two runs over the same inputs must not share one.
    const request = requestForInputs && { ...requestForInputs };
    if (!request) {
      setCalculationError(
        "Cannot calculate report: missing account or date range.",
      );
      return;
    }

    setPairFailures([]);
    const finishCalculation = startCalculation(calculationInputsKey);
    try {
      const started = await startBucketReportJob(request).unwrap();
      const job = await pollReportJob(started, (key) =>
        fetchBucketJobStatus({ key }).unwrap(),
      );
      if (job.status !== ReportJobStatus.COMPLETED || !job.result) {
        setCalculationError(
          reportJobErrorMessage(
            job,
            "Failed to calculate strategy report. Please try again.",
          ),
        );
        return;
      }
      setExcelUrl(job.result.excel_url);
      setExcelRequest(request);
      // Pairs without option history still succeed (portfolio-only sheet)
      // — only real errors pop up here.
      setPairFailures(
        job.result.reports.filter(
          (report) => report.status === BucketReportStatusEnum.ERROR,
        ),
      );
      // Name the pairing formula the run was produced by, for the reason the
      // summary names the run's own TWR formula (useSummaryCards' applyRun).
      setOptionPairingV2(job.result.option_pairing_v2 ?? optionPairingV2);
      applySummaryRun(
        job.result.bucket_totals ?? {},
        job.result.twr_formula,
        calculationInputsKey,
      );

      // The calculated themed-card values, each card applying its own share
      // of the payload. The Keep card goes first because the Diversify
      // card's stock switch offers the assets it adopted. Where a figure is
      // null -- a refusal, or a scope with no data -- the stored value stays,
      // and each card's applyRun says how its refusals are shown.
      const themed = job.result.themed_cards ?? {};
      const keepCard = applyKeepRun(themed.keep);
      applyProtectRun(themed.protect);
      applyDiversifyRun(themed.diversify, keepCard);
      applyHarvestRun(themed.harvest);
    } catch (e) {
      setCalculationError(
        extractReportErrorMessage(
          e,
          "Failed to calculate strategy report. Please try again.",
        ),
      );
    } finally {
      finishCalculation();
    }
  }, [
    startCalculation,
    calculationInputsKey,
    requestForInputs,
    startBucketReportJob,
    fetchBucketJobStatus,
    optionPairingV2,
    applySummaryRun,
    applyKeepRun,
    applyProtectRun,
    applyDiversifyRun,
    applyHarvestRun,
  ]);

  // Reset job outputs when key report inputs change: each card's
  // resetForNewInputs says what of its own goes, and why.
  useEffect(() => {
    setExcelUrl(null);
    setExcelRequest(null);
    setPairFailures([]);
    summary.resetForNewInputs();
    keep.resetForNewInputs();
    protect.resetForNewInputs();
    harvest.resetForNewInputs();
    diversify.resetForNewInputs();
    // The deps are the report's inputs and nothing else: the resets are
    // stable, so listing them would only restate that.
  }, [
    settingsInfo.orion_account_id,
    settingsInfo.period?.[0],
    settingsInfo.period?.[1],
    // Which Bucket 3 the report names is a job input like any other, and it
    // is changed on its own from Settings -- so it belongs here beside the
    // account. Missing, switching Bucket 3 left the harvest picker offering
    // the PREVIOUS bucket's tickers with its counts still on the two rows and
    // the caption still dating them, which is worse than the account case it
    // resembles: Bucket 3 may belong to a different account of the client
    // (see the request builder above), so those counts can be another
    // account's entirely, and they reach the client's PDF that way.
    settingsInfo.bucket_3_id,
    // A new rate means a new job — the ready excel belongs to the old one.
    stateTaxRate,
    federalTaxRate,
    // So does a new option-pairing formula: every option figure moves.
    optionPairingV2,
  ]);

  return (
    <Card>
      <CardContent>
        <RequestNotification
          open={Boolean(calculationError)}
          title="Calculation failed"
          message={calculationError}
          onClose={() => setCalculationError("")}
        />
        <RequestNotification
          open={Boolean(excelError)}
          title="Excel download failed"
          message={excelError}
          onClose={() => setExcelError("")}
        />
        <RequestNotification
          open={Boolean(pdfError)}
          title="PDF download failed"
          message={pdfError}
          onClose={() => setPdfError("")}
        />
        <RequestNotification
          open={pairFailures.length > 0}
          severity="warning"
          title={`${pairFailures.length} bucket report(s) failed`}
          message={pairFailuresMessage(pairFailures)}
          onClose={() => setPairFailures([])}
        />
        <SettingsReport
          type={PerformanceReportTypeEnum.STRATEGY}
          header="Optic Equity Access Performance"
          subtitle="OPTIC ASSET MANAGEMENT"
          // Any run in flight, not only the on-screen inputs' one: the client
          // is not changeable mid-calculation (task 4696).
          isClientLocked={isCalculating}
          settingsAction={
            <StrategyReportSettings
              disabled={isCalculating}
              twrFormula={twrFormula}
              onTwrFormulaChange={summary.handleTwrFormulaChange}
              optionPairingV2={optionPairingV2}
              onOptionPairingChange={setOptionPairingV2}
              periodStart={settingsInfo.period[0] || undefined}
            />
          }
        />
        <ReportActions
          isPdfLoading={isPdfLoading}
          pdfDisabled={pdfAvailability.disabled}
          pdfHint={pdfAvailability.hint}
          onDownloadPdf={handleDownloadPdf}
          backendFiguresAvailable={Boolean(runExcelUrl)}
          liveFormulasAvailable={Boolean(liveExcelRequest)}
          onDownloadExcel={handleDownloadExcel}
          isPreparingLiveExcel={isPreparingLiveExcel}
          isCalculating={isCalculatingTheseInputs}
          onCalculate={handleCalculate}
          onOpenTransactions={openTransactionModal}
        />
        {!isReportCalculated && (
          <CalculatePrompt isCalculating={isCalculatingTheseInputs} />
        )}
        {/* Hidden rather than unmounted, so what the reader picked on the
            charts survives the next run. */}
        <Box sx={{ display: isReportCalculated ? undefined : "none" }}>
          <ChartsReport
            bucketTotals={summary.bucketTotals}
            period={settingsInfo.period}
            twrFormula={twrFormula}
            onSpecsChange={setChartSpecs}
          />
          <Grid
            container
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 1 }}
          >
            <Typography variant="h6">Summary</Typography>
            <Grid item>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <BucketSummarySelect
                  value={summary.selectedSummary}
                  bucketTotals={summary.bucketTotals ?? undefined}
                  onChange={summary.handleSummaryChange}
                />
              </Box>
            </Grid>
          </Grid>
          <SummaryCardsGrid summary={summary} />
          <Grid container spacing={4} columns={{ lg: 12, md: 6, xs: 12 }}>
            {themedCards.map(({ config, blurb, chart }) => (
              <Grid item lg={6} xs={12} md={6} key={config.label}>
                <MultiValuesCard
                  title={config.label}
                  values={config.values}
                  headerAction={config.headerAction}
                  description={blurb}
                  additionalComponents={chart}
                />
              </Grid>
            ))}
          </Grid>
        </Box>
        <TransactionModal
          open={isTransactionModalOpen}
          onClose={closeTransactionModal}
          settingsInfo={settingsInfo}
          reportType={PerformanceReportTypeEnum.STRATEGY}
        />
      </CardContent>
    </Card>
  );
}

export const StrategyReport = () => (
  <ReportSettingsProvider>
    <StrategyReportContent />
  </ReportSettingsProvider>
);

export default StrategyReport;
